export type AuthOverride = {
  token: string
  onUnauthorized?: (method: string, url: string) => ApiError
  onForbidden?: (method: string, url: string) => ApiError
}

export type FetchOptions = {
  token?: string
  authOverride?: AuthOverride
  headers?: Record<string, string>
  skipAuthRefresh?: boolean
  signal?: AbortSignal
}

export type AuthTokenRefreshOptions = { allowInteractive?: boolean }
type AuthTokenRefreshHandler = (options?: AuthTokenRefreshOptions) => Promise<string | undefined>

let authTokenRefreshHandler: AuthTokenRefreshHandler | undefined
let authTokenRefreshPromise: Promise<string | undefined> | undefined

export function registerAuthTokenRefreshHandler(handler: AuthTokenRefreshHandler): () => void {
  authTokenRefreshHandler = handler

  return () => {
    if (authTokenRefreshHandler === handler) {
      authTokenRefreshHandler = undefined
    }
  }
}

// Proactively renew the device token. No-op when no OIDC handler is registered (embedded KDS,
// Admin), so only standalone KDS pre-warms its token after a permission change. Runs silently:
// a renewal needing interaction gives up quietly and leaves re-auth to the next user action.
export async function refreshDeviceToken(allowInteractive = false): Promise<void> {
  if (authTokenRefreshHandler) {
    await refreshToken(allowInteractive)
  }
}

export class ApiError extends Error {
  status: number
  body: unknown

  constructor(method: string, url: string, status: number, body: unknown) {
    super(`${method} ${url} failed with status ${status}`)
    this.status = status
    this.body = body
  }
}

export type StepUpAuthorizationErrorKind = 'invalid-token' | 'insufficient-permission'

export class StepUpAuthorizationError extends ApiError {
  readonly kind: StepUpAuthorizationErrorKind

  constructor(method: string, url: string, status: number, kind: StepUpAuthorizationErrorKind) {
    super(method, url, status, undefined)
    this.name = 'StepUpAuthorizationError'
    this.kind = kind
  }
}

// Standalone SPAs (KDS, Order Entry) load in same-origin iframes inside the Admin
// shell. The shell exposes its session/auth state on window.top.Page.
type ParentPage = {
  apiAuth?: Record<string, unknown>
  timedOut?: () => void
  updateSessionExpiry?: () => void
}

function parentPage(): ParentPage | undefined {
  try {
    return (window.top as unknown as { Page?: ParentPage } | null)?.Page
  } catch {
    return undefined
  }
}

async function refreshAdminToken(): Promise<string | undefined> {
  try {
    const res = await fetch('/system/getoauthtoken', { credentials: 'include' })
    if (res.status === 401) {
      parentPage()?.timedOut?.()
      return undefined
    }
    if (!res.ok) return undefined
    const data = (await res.json()) as { AccessToken?: string; ExpiresIn?: number }
    const page = parentPage()
    if (page && data.ExpiresIn != null) {
      page.apiAuth = { ...data, ExpiresAt: Date.now() + data.ExpiresIn * 1000 }
    }
    return data.AccessToken
  } catch {
    return undefined
  }
}

async function refreshToken(allowInteractive = true): Promise<string | undefined> {
  // Standalone KDS registers its cookie-backed OIDC renewal while it is mounted.
  // Embedded KDS and Admin keep using the legacy same-origin session endpoint.
  if (!authTokenRefreshHandler) {
    return refreshAdminToken()
  }

  if (!authTokenRefreshPromise) {
    authTokenRefreshPromise = authTokenRefreshHandler({ allowInteractive })
      .catch(() => undefined)
      .finally(() => {
        authTokenRefreshPromise = undefined
      })
  }

  return authTokenRefreshPromise
}

async function executeWithRefresh(
  options: FetchOptions | undefined,
  doFetch: (token: string | undefined) => Promise<Response>
): Promise<Response> {
  const initialToken = options?.authOverride?.token ?? options?.token
  let response = await doFetch(initialToken)

  const skipAuthRefresh = options?.skipAuthRefresh || options?.authOverride !== undefined
  const hasOidcRefreshHandler = authTokenRefreshHandler !== undefined
  const shouldRefresh =
    response.status === 401 || (response.status === 403 && hasOidcRefreshHandler)
  if (shouldRefresh && initialToken && !skipAuthRefresh) {
    const newToken = await refreshToken()
    if (newToken) response = await doFetch(newToken)
  }

  if (response.ok) parentPage()?.updateSessionExpiry?.()

  return response
}

// Many ASP.NET endpoints return Ok() with no body (content-length: 0).
// Calling response.json() on an empty body throws, so we check first.
function hasJsonBody(response: Response): boolean {
  if (response.status === 204) return false
  if (response.headers.get('content-length') === '0') return false
  return true
}

async function parseJson<T>(response: Response): Promise<T> {
  if (!hasJsonBody(response)) return undefined as T
  return response.json() as Promise<T>
}

async function tryParseJson(response: Response): Promise<unknown> {
  try {
    return await response.json()
  } catch {
    return undefined
  }
}

async function createApiError(
  method: string,
  url: string,
  response: Response,
  options: FetchOptions | undefined
): Promise<ApiError> {
  const override = options?.authOverride
  if (override) {
    if (response.status === 401 && override.onUnauthorized) {
      return override.onUnauthorized(method, url)
    }
    if (response.status === 403 && override.onForbidden) {
      return override.onForbidden(method, url)
    }
  }

  return new ApiError(method, url, response.status, await tryParseJson(response))
}

export async function fetchGet<T>(url: string, options?: FetchOptions): Promise<T> {
  const response = await executeWithRefresh(options, async token => {
    const headers: HeadersInit = {}
    if (token) headers['Authorization'] = `Bearer ${token}`
    const requestInit: RequestInit = { headers }
    if (options?.signal) requestInit.signal = options.signal
    return fetch(url, requestInit)
  })
  if (!response.ok) {
    throw await createApiError('GET', url, response, options)
  }
  return parseJson<T>(response)
}

export async function fetchPost<T>(
  url: string,
  data?: unknown,
  options?: FetchOptions
): Promise<T> {
  const response = await executeWithRefresh(options, async token => {
    const headers: HeadersInit = { 'Content-Type': 'application/json' }
    if (token) headers['Authorization'] = `Bearer ${token}`
    return fetch(url, {
      method: 'POST',
      headers: { ...headers, ...options?.headers },
      body: data != null ? JSON.stringify(data) : undefined,
    })
  })
  if (!response.ok) {
    throw await createApiError('POST', url, response, options)
  }
  return parseJson<T>(response)
}

export async function fetchPut<T>(url: string, data?: unknown, options?: FetchOptions): Promise<T> {
  const response = await executeWithRefresh(options, async token => {
    const headers: HeadersInit = { 'Content-Type': 'application/json' }
    if (token) headers['Authorization'] = `Bearer ${token}`
    return fetch(url, {
      method: 'PUT',
      headers,
      body: data != null ? JSON.stringify(data) : undefined,
    })
  })
  if (!response.ok) {
    throw await createApiError('PUT', url, response, options)
  }
  return parseJson<T>(response)
}
